"use client";
import React, { useEffect, useRef } from "react";
import { initializeApp, getApps, FirebaseApp } from "firebase/app";
import {
  getAuth,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  onAuthStateChanged,
  signOut,
  updateProfile as firebaseUpdateProfile,
  Auth,
} from "firebase/auth";
import { useDCAuthStore } from "./store";
import { DCAuthConfig, DCAuthMethods, DCAuthProviderProps } from "./types";

// Singleton para Firebase
let firebaseApp: FirebaseApp | null = null;
let firebaseAuth: Auth | null = null;
let googleProvider: GoogleAuthProvider | null = null;

const initializeFirebase = (config: DCAuthConfig) => {
  if (typeof window === "undefined") return null;
  
  if (!firebaseApp) {
    firebaseApp = getApps().length === 0 ? initializeApp(config) : getApps()[0];
    firebaseAuth = getAuth(firebaseApp);
    googleProvider = new GoogleAuthProvider();
  }
  
  return { auth: firebaseAuth!, provider: googleProvider! };
};

export const DCAuth: React.FC<DCAuthProviderProps> = ({ 
  children, 
  config,
  callbacks = {}
}) => {
  const { setUser, setLoading, setError } = useDCAuthStore();
  const authMethodsRef = useRef<DCAuthMethods | null>(null);
  const previousUserRef = useRef<any>(null);
  const callbacksRef = useRef(callbacks);

  // Actualizar callbacks ref cuando cambien
  useEffect(() => {
    callbacksRef.current = callbacks;
  }, [callbacks]);

  useEffect(() => {
    // Solo inicializar en el cliente
    if (typeof window === "undefined") return;

    // Usar config prop o env vars como fallback
    const firebaseConfig: DCAuthConfig = config || {
      apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY!,
      authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN!,
      projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID!,
      storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET!,
      messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID!,
      appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID!,
    };

    const firebase = initializeFirebase(firebaseConfig);
    if (!firebase) return;

    const { auth, provider } = firebase;

    const loginWithEmail = async (email: string, password: string) => {
      try {
        await signInWithEmailAndPassword(auth, email, password);
        setError(null);
      } catch (error) {
        const err = error as Error;
        console.error("Error al iniciar sesión con email:", err);
        setError(err);
        if (callbacksRef.current?.onError) {
          callbacksRef.current.onError(err);
        }
        throw error;
      }
    };

    const loginWithGoogle = async () => {
      try {
        await signInWithPopup(auth, provider);
        setError(null);
      } catch (error) {
        const err = error as Error;
        console.error("Error al iniciar sesión con Google:", err);
        setError(err);
        if (callbacksRef.current?.onError) {
          callbacksRef.current.onError(err);
        }
        throw error;
      }
    };

    const logout = async () => {
      try {
        await signOut(auth);
        setError(null);
      } catch (error) {
        const err = error as Error;
        console.error("Error al cerrar sesión:", err);
        setError(err);
        if (callbacksRef.current?.onError) {
          callbacksRef.current.onError(err);
        }
        throw error;
      }
    };

    const updateProfile = async (profile: { displayName?: string; photoURL?: string }) => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) {
          throw new Error("No hay usuario autenticado");
        }
        await firebaseUpdateProfile(currentUser, profile);
        // Actualizar el user en el store
        setUser({ ...currentUser });
        setError(null);
      } catch (error) {
        const err = error as Error;
        console.error("Error al actualizar perfil:", err);
        setError(err);
        if (callbacksRef.current?.onError) {
          callbacksRef.current.onError(err);
        }
        throw error;
      }
    };

    authMethodsRef.current = { loginWithEmail, loginWithGoogle, logout, updateProfile };

    // Inyecta funciones en window para accesibilidad sin hooks
    if (typeof window !== "undefined") {
      (window as any).dcAuth = authMethodsRef.current;
    }

    // Escuchar cambios de autenticación
    const unsub = onAuthStateChanged(auth, (user) => {
      const previousUser = previousUserRef.current;
      previousUserRef.current = user;
      
      setUser(user);
      setLoading(false);

      // Callback de cambio de estado (sin await para evitar bloqueos)
      if (callbacksRef.current?.onAuthStateChange) {
        callbacksRef.current.onAuthStateChange(user);
      }

      // onLogin - solo si cambió de null a user
      if (!previousUser && user && callbacksRef.current?.onLogin) {
        callbacksRef.current.onLogin(user);
      }

      // onLogout - solo si cambió de user a null
      if (previousUser && !user && callbacksRef.current?.onLogout) {
        callbacksRef.current.onLogout();
      }
    });

    return () => unsub();
  }, [config]);

  return <>{children}</>;
};
